/**
 * Utility functions for handling dates and timestamps
 */

import { getActiveLocale } from './activeLocale';

/**
 * Converts a UTC timestamp string to a Date object in the user's local timezone
 * @param utcTimestamp - UTC timestamp string (e.g., "2025-03-18T16:13:16.257000+00:00")
 * @returns Date object in the user's local timezone
 */
export const toLocalDate = (utcTimestamp: string): Date => {
  if (!utcTimestamp) {
    return new Date();
  }

  try {
    // Handle standard Firebase timestamp format with +00:00 timezone and microseconds
    if (utcTimestamp.includes('+00:00')) {
      // First, clean up the timestamp by handling microseconds correctly
      // Firebase uses format like: 2025-03-19T11:48:14.461000+00:00
      const cleanedTimestamp = utcTimestamp.replace(/\.(\d{3})(\d{3})\+00:00/, '.$1Z');

      // If the regex didn't match, just replace +00:00 with Z
      if (cleanedTimestamp === utcTimestamp) {
        return new Date(utcTimestamp.replace('+00:00', 'Z'));
      }

      return new Date(cleanedTimestamp);
    }

    // For all other timestamp formats, let JavaScript handle it
    return new Date(utcTimestamp);
  } catch (error) {
    console.error('Error parsing timestamp:', error, utcTimestamp);
    return new Date(); // Return current time as fallback
  }
};

/**
 * Formats a UTC timestamp string into a human-readable format in the user's local timezone
 * @param utcTimestamp - UTC timestamp string
 * @param options - Intl.DateTimeFormatOptions for customizing the output
 * @returns Formatted date string in the user's local timezone
 */
export const formatLocalDateTime = (
  utcTimestamp: string,
  options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }
): string => {
  const localDate = toLocalDate(utcTimestamp);
  return new Intl.DateTimeFormat(navigator.language, options).format(localDate);
};

/**
 * Formats a UTC timestamp string as a time only in the user's local timezone
 * @param utcTimestamp - UTC timestamp string
 * @returns Formatted time string (e.g., "3:45 PM")
 */
export const formatLocalTime = (utcTimestamp: string): string => {
  const localDate = toLocalDate(utcTimestamp);
  return localDate.toLocaleTimeString(navigator.language, {
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * The "just now" bucket, kept as an explicit map rather than going through
 * `Intl.RelativeTimeFormat`.
 *
 * `Intl.RelativeTimeFormat(locale, { numeric: 'auto' }).format(0, 'second')`
 * returns "jetzt" / "now" — correct, but it would silently reword chat's
 * long-standing German copy. Every other bucket below IS delegated to `Intl`,
 * because there its German output is byte-identical to the hand-written
 * strings this function used to hardcode ("vor 1 Minute", "vor 5 Minuten",
 * "vor 1 Stunde", "vor 2 Stunden") while also being correct in English and
 * correctly pluralised in any locale added later.
 *
 * A locale with no entry here falls back to `Intl`'s own wording.
 */
const JUST_NOW: Record<string, string> = {
  de: 'gerade eben',
  en: 'just now',
};

/**
 * Resolves the locale display should follow: the language the user picked in
 * the app, not the browser's.
 *
 * This function used to read `navigator.language` for its absolute-date
 * branch and hardcode German for its relative branches, so an English UI on a
 * German machine rendered German either way — and, because the branches
 * disagreed, a single list could mix languages by row age.
 *
 * The active locale is read through `utils/activeLocale` — a registry
 * `src/i18n.ts` pushes into — rather than by importing the i18n singleton
 * here. That keeps this module free of i18next's unparseable type
 * declarations (see `activeLocale.ts`), and it is read per call rather than
 * captured at module scope so a later `changeLanguage` takes effect.
 *
 * The explicit override exists mainly so tests can assert both languages
 * without driving a global; production call sites pass nothing.
 */
function resolveLocale(explicit?: string): string {
  return explicit || getActiveLocale();
}

/**
 * Formats a date as a relative time (e.g., "2 hours ago", "vor 2 Stunden")
 * Specifically handles Firebase timestamps with +00:00 format
 *
 * @param dateString - ISO date string or Firebase timestamp with +00:00 format
 * @param locale - Optional BCP-47 override; defaults to the app's active language
 * @returns Formatted relative time string
 */
export const formatRelativeDateTime = (dateString: string, locale?: string): string => {
  if (!dateString) {
    console.error('Empty date string provided to formatRelativeDateTime');
    return '';
  }

  try {
    // console.log('formatRelativeDateTime input:', dateString);

    let date: Date;

    // Handle Firebase timestamp format with microseconds and +00:00 timezone
    if (dateString.includes('+00:00')) {
      // Extract date parts from format like: 2025-03-19T13:45:16.317000+00:00
      const match = dateString.match(/(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2}):(\d{2})\.(\d+)\+00:00/);

      if (match) {
        const [_, datePart, hours, minutes, seconds, fractionalSeconds] = match;

        // Only use the first 3 digits of fractional seconds (milliseconds)
        const milliseconds = fractionalSeconds.substring(0, 3);

        // Create a new Date object with the parts - use Z suffix for UTC
        date = new Date(`${datePart}T${hours}:${minutes}:${seconds}.${milliseconds}Z`);
        // console.log('Parsed Firebase timestamp to date:', date);
      } else {
        // If the regex doesn't match, fallback to standard parsing
        date = new Date(dateString);
      }
    } else {
      // Regular ISO string
      date = new Date(dateString);
    }

    if (isNaN(date.getTime())) {
      throw new Error(`Invalid date: ${dateString}`);
    }

    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    const activeLocale = resolveLocale(locale);
    // `numeric: 'auto'` is what yields "vor 2 Stunden" / "2 hours ago" rather
    // than the stilted "in -2 Stunden" form.
    const relative = new Intl.RelativeTimeFormat(activeLocale, { numeric: 'auto' });

    // Thresholds are unchanged from the hand-written version they replaced:
    // under a minute, under an hour, under 12 hours, then an absolute date.
    if (diffInSeconds < 60) {
      const baseLanguage = activeLocale.split('-')[0];
      return JUST_NOW[baseLanguage] ?? relative.format(0, 'second');
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return relative.format(-minutes, 'minute');
    } else if (diffInSeconds < 43200) {
      // 12 hours = 43200 seconds
      const hours = Math.floor(diffInSeconds / 3600);
      return relative.format(-hours, 'hour');
    } else {
      // For anything older than 12 hours, show the complete date and time —
      // in the app's language, so it agrees with the branches above.
      return date.toLocaleDateString(activeLocale, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    }
  } catch (e) {
    console.error('Error formatting relative date:', e, dateString);
    // Return the original string if we can't parse it
    return dateString;
  }
};

/**
 * Formats a UTC string timestamp from Firebase to local date and time
 * Handles the Firebase UTC format with +00:00 suffix
 *
 * @param firebaseTimestamp - Firebase UTC timestamp (e.g., "2025-03-18T16:13:16.257000+00:00")
 * @param format - Optional format specifier: 'time', 'date', 'datetime', or 'relative'
 * @returns Formatted timestamp in the user's local timezone
 */
export const formatFirebaseTimestamp = (
  firebaseTimestamp: string,
  format: 'time' | 'date' | 'datetime' | 'relative' = 'datetime'
): string => {
  if (!firebaseTimestamp) {
    return '';
  }

  try {
    switch (format) {
      case 'time':
        return formatLocalTime(firebaseTimestamp);

      case 'date':
        return toLocalDate(firebaseTimestamp).toLocaleDateString(navigator.language, {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        });

      case 'relative':
        return formatRelativeDateTime(firebaseTimestamp);

      case 'datetime':
      default:
        return formatLocalDateTime(firebaseTimestamp);
    }
  } catch (e) {
    console.warn('Error formatting Firebase timestamp:', e);
    return '';
  }
};

/**
 * Formats a timestamp for display in the chat UI
 * Specifically handles Firebase timestamps with +00:00 format
 *
 * @param timestamp - The createdAt string from Firebase
 * @returns A properly formatted time string in the user's local timezone
 * @throws Error if the timestamp cannot be converted
 */
export const formatChatTime = (timestamp: string): string => {
  if (!timestamp) {
    throw new Error('Missing timestamp for formatting');
  }

  try {
    // Debug
    // console.log('formatChatTime input:', timestamp);

    // Special handling for Firebase timestamp format with microseconds and +00:00 timezone
    if (timestamp.includes('+00:00')) {
      // Extract date parts from format like: 2025-03-19T11:39:09.519000+00:00
      const match = timestamp.match(/(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2}):(\d{2})\.(\d+)\+00:00/);

      if (match) {
        const [_, date, hours, minutes, seconds, fractionalSeconds] = match;

        // Only use the first 3 digits of fractional seconds (milliseconds)
        const milliseconds = fractionalSeconds.substring(0, 3);

        // Create a new Date object with the parts
        // This will automatically convert to local time
        const localDate = new Date(`${date}T${hours}:${minutes}:${seconds}.${milliseconds}Z`);
        // console.log('Parsed date:', localDate);

        // Format the date and time based on user's locale
        return localDate.toLocaleDateString(navigator.language, {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        });
      }
    }

    // For any other timestamp format
    const localDate = new Date(timestamp);

    if (isNaN(localDate.getTime())) {
      throw new Error(`Invalid date: ${timestamp}`);
    }

    return localDate.toLocaleDateString(navigator.language, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch (e) {
    console.error('Error formatting chat time:', e, timestamp);
    throw e;
  }
};
